import { faAngleDown, faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown, DropdownContent, DropdownTrigger } from 'components/UI/V3/Dropdown';
import { DropdownItem } from 'components/UI/V3/Dropdown/Item/DropdownItem';
import { useAuth } from 'hooks';
import { FunctionComponent, useMemo, useState } from 'react';
import styles from './MarketplaceSelector.module.scss';

interface MarketplaceSelectorProps {
    selectedMarketplaceId: number;
    onChange: (marketplaceId: number) => void;
}

export const useShowMarketplaceSelector = (): boolean => {
    const auth = useAuth();
    return (auth.marketplaces || []).length > 1;
};

export const MarketplaceSelector: FunctionComponent<MarketplaceSelectorProps> = props => {
    const auth = useAuth();
    const [isOpen, setIsOpen] = useState(false);

    const marketplaces = useMemo(
        () => [...(auth.marketplaces || [])].sort((a, b) => a.name.localeCompare(b.name)),
        [auth.marketplaces]
    );

    const selectedMarketplace = marketplaces.find(marketplace => marketplace.id === props.selectedMarketplaceId);

    if (marketplaces.length <= 1 || !selectedMarketplace) {
        return null;
    }

    const handleSelect = (marketplaceId: number) => {
        setIsOpen(false);

        if (marketplaceId === props.selectedMarketplaceId) {
            return;
        }

        props.onChange(marketplaceId);
    };

    return (
        <div className={styles.root}>
            <Dropdown
                open={isOpen}
                onOpenChange={setIsOpen}
            >
                <DropdownTrigger className={styles.trigger}>
                    <FontAwesomeIcon
                        icon={faLocationDot}
                        className={styles.icon}
                    />
                    <span className={styles.name}>{selectedMarketplace.name}</span>
                    <FontAwesomeIcon
                        icon={faAngleDown}
                        className={styles.chevron}
                    />
                </DropdownTrigger>

                <DropdownContent
                    align="start"
                    maxHeight={300}
                >
                    {marketplaces.map(marketplace => (
                        <DropdownItem
                            key={marketplace.id}
                            className={styles.option}
                            onSelect={() => handleSelect(marketplace.id)}
                        >
                            {marketplace.name}
                        </DropdownItem>
                    ))}
                </DropdownContent>
            </Dropdown>
        </div>
    );
};

export default MarketplaceSelector;
